import React, { useState } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  UseOfForceAuthorizationModel,
  UseOfForceAuthorizationStatus,
} from '../../../../api-service/clients';
import { ConfirmDialog } from '../../../../components/confirm-dialog';
import { formatDate, formatDateTime } from '../../../../utilities';
import {
  authorizationDecisionMakerLabels,
  authorizationStatusLabels,
  authorizationTypeShortLabels,
} from './use-of-force-labels';

interface UseOfForceAuthorizationsTableProps {
  authorizations: UseOfForceAuthorizationModel[];
  showServiceProviderUnit?: boolean;
  canRevoke: (authorization: UseOfForceAuthorizationModel) => boolean;
  onRevoke: (authorization: UseOfForceAuthorizationModel) => Promise<void>;
  renderActions?: (authorization: UseOfForceAuthorizationModel) => React.ReactNode;
}

export function UseOfForceAuthorizationsTable(
  props: UseOfForceAuthorizationsTableProps
) {
  const {
    authorizations,
    showServiceProviderUnit,
    canRevoke,
    onRevoke,
    renderActions,
  } = props;
  const [revoking, setRevoking] = useState<UseOfForceAuthorizationModel>();
  const [isRevoking, setIsRevoking] = useState(false);

  async function revoke(authorization: UseOfForceAuthorizationModel) {
    setIsRevoking(true);
    try {
      await onRevoke(authorization);
      setRevoking(undefined);
    } finally {
      setIsRevoking(false);
    }
  }

  if (authorizations.length === 0) {
    return (
      <Typography variant="body2">
        Der er ingen afgørelser om forhåndsgodkendelse.
      </Typography>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Indgreb</TableCell>
            <TableCell>Periode</TableCell>
            <TableCell>Truffet af</TableCell>
            {showServiceProviderUnit && <TableCell>Udførerenhed</TableCell>}
            <TableCell>Status</TableCell>
            <TableCell>Registreret</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {authorizations.map(authorization => (
            <TableRow key={authorization.id}>
              <TableCell>
                {authorizationTypeShortLabels[authorization.authorizationType]}
              </TableCell>
              <TableCell>{`${formatDate(
                authorization.periodFrom
              )} - ${formatDate(authorization.periodTo)}`}</TableCell>
              <TableCell>
                {authorizationDecisionMakerLabels[authorization.decisionMaker]}
              </TableCell>
              {showServiceProviderUnit && (
                <TableCell>
                  {authorization.serviceProviderUnitName || '-'}
                </TableCell>
              )}
              <TableCell>
                {authorizationStatusLabels[authorization.status]}
                {authorization.revokedAt && (
                  <Typography variant="caption" display="block">
                    {nameAndTime(
                      authorization.revokedByName,
                      authorization.revokedAt
                    )}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {nameAndTime(
                  authorization.registeredByName,
                  authorization.registeredAt
                )}
                <Typography variant="caption" display="block">
                  {registeredFrom(authorization)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {renderActions?.(authorization)}
                {authorization.status ===
                  UseOfForceAuthorizationStatus.Active &&
                  canRevoke(authorization) && (
                    <Button
                      size="small"
                      onClick={() => setRevoking(authorization)}
                    >
                      Tilbagekald
                    </Button>
                  )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {revoking && (
        <ConfirmDialog
          open
          title="Tilbagekald afgørelse"
          message={`Afgørelsen om ${
            authorizationTypeShortLabels[revoking.authorizationType]
          } holder op med at gælde i dag. Det kan ikke fortrydes.`}
          confirmButtonText="Tilbagekald"
          cancelButtonText="Annullér"
          isLoading={isRevoking}
          type="warning"
          onConfirmClick={() => revoke(revoking)}
          onCancelClick={() => setRevoking(undefined)}
        />
      )}
    </>
  );
}

function registeredFrom(authorization: UseOfForceAuthorizationModel) {
  if (authorization.isFromRequest) return 'Fra Skema 3';
  return authorization.registeredByServiceProvider
    ? 'Fra handlekommunens brev'
    : 'Eksisterende afgørelse';
}

function nameAndTime(
  name: string | undefined,
  time: Date | string | undefined
) {
  return (
    [name, time ? formatDateTime(time) : undefined]
      .filter(Boolean)
      .join(', ') || '-'
  );
}
